import { useEffect, useRef, useState } from 'react'
import * as attachmentsApi from '@/api/attachments'
import * as settingsApi from '@/api/settings'

function formatBytes(n) {
  const bytes = Number(n) || 0
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatWhen(iso) {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ''
  }
}

/**
 * Page-level attachments strip for a customer category.
 */
export function CategoryAttachments({ customerId, categorySlug, canEdit }) {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState('')
  const [maxLabel, setMaxLabel] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const [list, settings] = await Promise.all([
          attachmentsApi.listAttachments(customerId, categorySlug),
          canEdit
            ? settingsApi.getSiteSettings().catch(() => null)
            : Promise.resolve(null),
        ])
        if (!cancelled) {
          setItems(list)
          if (settings?.attachment_max_bytes) {
            setMaxLabel(
              `Max ${Math.round(settings.attachment_max_bytes / (1024 * 1024))} MB each`,
            )
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'Could not load attachments.',
          )
          setItems([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [customerId, categorySlug, canEdit])

  async function handleUpload(e) {
    const files = [...(e.target.files || [])]
    e.target.value = ''
    if (!files.length || !canEdit) return
    setUploading(true)
    setError('')
    setUploadProgress('')
    const created = []
    const failures = []
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i]
        setUploadProgress(`Uploading ${i + 1} of ${files.length}…`)
        try {
          const row = await attachmentsApi.uploadAttachment(
            customerId,
            categorySlug,
            file,
          )
          created.push(row)
        } catch (err) {
          const detail =
            typeof err.response?.data?.detail === 'string'
              ? err.response.data.detail
              : 'upload failed'
          failures.push(`${file.name}: ${detail}`)
        }
      }
      if (created.length) {
        setItems((prev) => [...created, ...prev])
      }
      if (failures.length) {
        setError(
          failures.length === 1
            ? failures[0]
            : `${failures.length} files failed. ${failures.slice(0, 3).join(' · ')}${
                failures.length > 3 ? '…' : ''
              }`,
        )
      }
    } finally {
      setUploading(false)
      setUploadProgress('')
    }
  }

  async function handleDownload(item) {
    setError('')
    try {
      await attachmentsApi.downloadAttachment(
        customerId,
        categorySlug,
        item.id,
        item.original_filename,
      )
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not download file.',
      )
    }
  }

  async function handleDelete(item) {
    if (!canEdit) return
    if (!window.confirm(`Remove “${item.original_filename}”?`)) return
    setError('')
    try {
      await attachmentsApi.deleteAttachment(customerId, categorySlug, item.id)
      setItems((prev) => prev.filter((a) => a.id !== item.id))
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not remove file.',
      )
    }
  }

  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-brand-muted">
            Attachments
          </p>
          {maxLabel && canEdit && (
            <p className="text-[11px] text-brand-muted">{maxLabel}</p>
          )}
        </div>
        {canEdit && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleUpload}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-brand hover:bg-slate-100 disabled:opacity-60"
            >
              {uploading
                ? uploadProgress || 'Uploading…'
                : 'Add attachments'}
            </button>
          </>
        )}
      </div>

      {error && (
        <p className="mt-2 text-xs text-red-700" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p className="mt-2 text-xs text-brand-muted">Loading attachments…</p>
      ) : items.length === 0 ? (
        <p className="mt-2 text-xs text-brand-muted">
          {canEdit
            ? 'No attachments yet. Add invoices, PDFs or images for this page.'
            : 'No attachments on this page.'}
        </p>
      ) : (
        <ul className="mt-2 flex flex-wrap gap-2">
          {items.map((item) => (
            <li
              key={item.id}
              className="flex max-w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-sm shadow-sm"
            >
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => handleDownload(item)}
                  className="block max-w-[14rem] truncate font-medium text-brand hover:underline sm:max-w-[18rem]"
                  title={item.original_filename}
                >
                  {item.original_filename}
                </button>
                <p className="text-[11px] text-brand-muted">
                  {item.extension?.toUpperCase()} · {formatBytes(item.size_bytes)}
                  {item.created_at ? ` · ${formatWhen(item.created_at)}` : ''}
                </p>
              </div>
              <div className="flex shrink-0 flex-col gap-0.5 sm:flex-row sm:gap-2">
                <button
                  type="button"
                  onClick={() => handleDownload(item)}
                  className="text-xs font-medium text-brand hover:underline"
                >
                  Download
                </button>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleDelete(item)}
                    className="text-xs font-medium text-red-700 hover:underline"
                  >
                    Remove
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
